/**
 * notify
 *
 * Locale-aware toast helper that wraps Sonner with three guarantees:
 *
 * 1. **Locale resolution**: every toast string can be passed as a stable
 *    message key (e.g. `comment.added`) along with an optional inline
 *    fallback text used until/unless a key is registered.
 * 2. **Intent -> duration mapping**: callers declare semantic intent
 *    (`transient` | `important` | `destructive`) and the helper picks
 *    the appropriate Sonner duration so we no longer rely on Sonner's
 *    1800ms default for important confirmations.
 * 3. **Style consistency**: a single call site applies our message style
 *    rules (sentence case, no trailing period) and structured logging.
 *
 * See `docs/architecture/forms-standard.md` section 4 for the policy.
 *
 * Direct usage of `toast.*` is reserved for realtime previews
 * (`useRealtimeToasts`) and the undo-affordance helper (`toastUndo`)
 * which need `toast.custom`/`toast.dismiss` semantics.
 */

import { toast } from "sonner";
import type { ExternalToast } from "sonner";
import { getFormsUiStrings, resolveFormsLocale } from "i18n/formsUi";
import {
  normalizeApiError,
  type NormalizedApiError,
  type NormalizeApiErrorOptions,
} from "lib/errors/normalizeApiError";

/**
 * Semantic intent for a transactional toast. Drives default duration and
 * close-button behavior. Forms should default to `important` on success
 * (4.5s, no forced close button) and `destructive` on user-actionable
 * errors (6s + forced close button so the user can dismiss explicitly).
 */
export type NotifyIntent = "transient" | "important" | "destructive";

/**
 * Duration in milliseconds per intent. Centralized so the policy can be
 * tuned in one place rather than scattered across call sites.
 */
export const INTENT_DURATIONS: Record<NotifyIntent, number> = {
  transient: 1800,
  important: 4500,
  destructive: 6000,
};

/**
 * Optional structured context attached to a notification for
 * observability. The `requestId` is forwarded to Sonner's `description`
 * slot when present so support can correlate failures, and recorded via
 * `console.warn`/`console.info` so log scrapers can ingest it.
 */
export interface NotifyContext {
  /** Surface that emitted the toast (e.g. "auth-modal", "thread-form-base"). */
  surface?: string;
  /** Request id from API error envelope, when present. */
  requestId?: string;
  /** Stable error/event code from the backend or schema. */
  code?: string;
}

/**
 * Either a stable message key (for forms-standard adapters) plus
 * arguments for ICU-like substitution, OR an inline fallback string.
 *
 * For the foundation slice we accept both shapes. As `notify` adoption
 * grows, callers should converge on `key` so we can enforce key
 * coverage at typecheck time per locale adapter.
 */
export interface NotifyMessage {
  /** Stable message id resolved through the relevant i18n adapter. */
  key?: string;
  /** Inline fallback text used when no adapter has resolved the key. */
  fallback?: string;
  /** ICU-style {arg} substitutions applied to the resolved string. */
  args?: Record<string, string | number>;
}

export interface NotifyOptions {
  /** Semantic intent. Defaults: success=important, error=destructive, info=transient. */
  intent?: NotifyIntent;
  /** Optional structured context (forwarded to logs + Sonner description). */
  context?: NotifyContext;
  /**
   * Optional override for Sonner's id, useful for de-duplicating
   * repeated identical toasts (e.g. retry storms).
   */
  id?: string | number;
}

/**
 * Adapter that resolves a message key to a localized string. Domain
 * adapters (authUi, draftUi, ...) register themselves here so `notify`
 * can call them generically. We use a runtime registry rather than a
 * compile-time union because CRA does not yet have a single
 * `messages.ts` schema; this keeps the helper portable.
 */
type LocaleResolver = (key: string, args?: Record<string, string | number>) => string | null;

const resolvers: LocaleResolver[] = [];

/**
 * Register an i18n adapter as a potential resolver for `notify` keys.
 * Adapters return `null` for keys they do not recognize; the first
 * non-null wins.
 *
 * Usage from a domain adapter (illustrative):
 *
 * ```ts
 * registerNotifyResolver((key, args) => {
 *   if (!key.startsWith("comment.")) return null;
 *   return formatComment(getCommentUiStrings(), key.slice("comment.".length), args);
 * });
 * ```
 */
export function registerNotifyResolver(resolver: LocaleResolver): () => void {
  resolvers.push(resolver);
  return () => {
    const idx = resolvers.indexOf(resolver);
    if (idx >= 0) {
      resolvers.splice(idx, 1);
    }
  };
}

/** Apply ICU-style `{arg}` substitution to a template string. */
function interpolate(template: string, args?: Record<string, string | number>): string {
  if (!args) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = args[name];
    return value === undefined || value === null ? `{${name}}` : String(value);
  });
}

/** Resolve a message through registered adapters or fall back. */
function resolveMessage(message: NotifyMessage): string {
  if (message.key) {
    for (const resolver of resolvers) {
      try {
        const resolved = resolver(message.key, message.args);
        if (resolved != null) {
          return interpolate(resolved, message.args);
        }
      } catch (err) {
        // Resolver errors are non-fatal; keep trying others, fall back to inline copy.
        // Logged at warn for visibility but never thrown.
        // eslint-disable-next-line no-console
        console.warn("[notify] resolver threw", err);
      }
    }
  }
  if (message.fallback) {
    return interpolate(message.fallback, message.args);
  }
  // Last-resort guard: surface the raw key so a missing string is at
  // least visible and can be triaged from a screenshot.
  return message.key ?? getFormsUiStrings().unexpectedError;
}

/** Build the Sonner ExternalToast options from intent + context. */
function buildToastOptions(intent: NotifyIntent, options?: NotifyOptions): ExternalToast {
  const out: ExternalToast = {
    duration: INTENT_DURATIONS[intent],
  };
  if (intent === "destructive") {
    out.closeButton = true;
  }
  if (options?.context?.requestId) {
    out.description = `Request id: ${options.context.requestId}`;
  }
  if (options?.id !== undefined) {
    out.id = options.id;
  }
  return out;
}

/**
 * Emit a structured log entry for the toast so observability tooling
 * can correlate user-visible feedback with server logs.
 */
function logToast(level: "success" | "error" | "info", text: string, intent: NotifyIntent, options?: NotifyOptions): void {
  const payload = {
    level,
    intent,
    text,
    surface: options?.context?.surface,
    code: options?.context?.code,
    requestId: options?.context?.requestId,
    locale: resolveFormsLocale(),
  };
  if (level === "error") {
    // eslint-disable-next-line no-console
    console.warn("[notify]", payload);
  } else {
    // eslint-disable-next-line no-console
    console.info("[notify]", payload);
  }
}

function show(level: "success" | "error" | "info", message: NotifyMessage, options?: NotifyOptions): string | number {
  const intent: NotifyIntent =
    options?.intent ?? (level === "success" ? "important" : level === "error" ? "destructive" : "transient");
  const text = resolveMessage(message);
  const toastOptions = buildToastOptions(intent, options);
  logToast(level, text, intent, options);
  if (level === "success") {
    return toast.success(text, toastOptions);
  }
  if (level === "error") {
    return toast.error(text, toastOptions);
  }
  return toast.info(text, toastOptions);
}

interface NotifyPromiseMessages {
  loading: NotifyMessage;
  success: NotifyMessage | ((data: unknown) => NotifyMessage);
  error: NotifyMessage | ((err: unknown) => NotifyMessage);
}

export const notify = {
  success(message: NotifyMessage, options?: NotifyOptions): string | number {
    return show("success", message, options);
  },
  error(message: NotifyMessage, options?: NotifyOptions): string | number {
    return show("error", message, options);
  },
  info(message: NotifyMessage, options?: NotifyOptions): string | number {
    return show("info", message, options);
  },
  /**
   * Pass-through to Sonner's `toast.promise` with locale-aware messages.
   * Loading uses `transient` duration, success/error use `important`/
   * `destructive` so duration policy stays consistent.
   */
  promise<T>(promise: Promise<T>, messages: NotifyPromiseMessages, options?: NotifyOptions): string | number {
    const loadingText = resolveMessage(messages.loading);
    const id = toast.promise(promise, {
      loading: loadingText,
      success: (data: T) => {
        const msg = typeof messages.success === "function" ? messages.success(data) : messages.success;
        const text = resolveMessage(msg);
        logToast("success", text, options?.intent ?? "important", options);
        return text;
      },
      error: (err: unknown) => {
        const msg = typeof messages.error === "function" ? messages.error(err) : messages.error;
        const text = resolveMessage(msg);
        logToast("error", text, options?.intent ?? "destructive", options);
        return text;
      },
      duration: INTENT_DURATIONS[options?.intent ?? "important"],
    });
    return id as unknown as string | number;
  },
  dismiss(id?: string | number): void {
    if (id === undefined) {
      toast.dismiss();
    } else {
      toast.dismiss(id);
    }
  },
  /**
   * Normalize an API/HTTP error AND emit a toast in one call.
   *
   * Pulls the user-facing message via `lib/errors/normalizeApiError`
   * (which prefers backend `response.data.message` over axios's
   * technical `"Request failed with status code N"`), threads the
   * extracted `requestId` and `code` into the toast's `context` so
   * the Sonner description shows "Request id: ..." for support
   * correlation, and returns the structured `NormalizedApiError` so
   * callers can ALSO render an inline banner if they want.
   *
   * Typical use:
   *   try { await api.post(...) }
   *   catch (err) {
   *     const normalized = notify.errorFromApi(err, {
   *       fallback: ui.somethingWentWrong,
   *       codeMap: { INVALID_CREDENTIALS: ui.invalidCreds },
   *     });
   *     setInlineError(normalized.message);
   *   }
   */
  errorFromApi(
    error: unknown,
    options: NormalizeApiErrorOptions & { surface?: string }
  ): NormalizedApiError {
    const normalized = normalizeApiError(error, options);
    show(
      "error",
      { fallback: normalized.message },
      {
        context: {
          surface: options.surface,
          requestId: normalized.requestId,
          code: normalized.code,
        },
      }
    );
    return normalized;
  },
};

export type Notify = typeof notify;
